import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { FaSave, FaVial } from 'react-icons/fa';
import { useTheme } from '../ThemeContext';
import StaffStatus from './StaffStatus';

function AISettings() {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({
    provider: 'openai',
    model: 'gpt-5.4',
    enabled: false,
    has_api_key: false
  });
  const [apiKey, setApiKey] = useState('');

  const adminToken = localStorage.getItem('Admintoken');

  const loadSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/s1/admin/ai-settings', {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load AI settings');
      setSettings(data.settings);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch('/s1/admin/ai-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          provider: 'openai',
          model: settings.model,
          enabled: settings.enabled,
          apiKey: apiKey.trim() || undefined
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save AI settings');
      setSettings(data.settings);
      setApiKey('');
      setMessage('AI settings saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const testSettings = async () => {
    setTesting(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch('/s1/admin/ai-settings/test', {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.details || 'OpenAI test failed');
      setMessage(data.message || 'OpenAI connection succeeded.');
    } catch (err) {
      setError(err.message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className={isDarkMode ? 'text-light' : ''}>
      <StaffStatus on={settings.enabled && settings.has_api_key}>
        {settings.enabled && settings.has_api_key ? 'Enabled' : 'Disabled'}
      </StaffStatus>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {message && <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert>}

      <Card className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}>
        <Card.Body>
          <Row className="g-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Provider</Form.Label>
                <Form.Control value="OpenAI" disabled />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Model</Form.Label>
                <Form.Control
                  value={settings.model || 'gpt-5.4'}
                  onChange={(e) => setSettings(prev => ({ ...prev, model: e.target.value }))}
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>OpenAI API Key</Form.Label>
                <Form.Control
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={settings.has_api_key ? 'Existing key saved. Enter a new key to replace it.' : 'Enter OpenAI API key'}
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                />
                <Form.Text className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                  Stored encrypted server-side. The key is never returned to the browser.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Check
                type="switch"
                id="ai-report-builder-enabled"
                label="Enable AI Report Builder for users with Build Your Own Reports access"
                checked={!!settings.enabled}
                onChange={(e) => setSettings(prev => ({ ...prev, enabled: e.target.checked }))}
              />
            </Col>
          </Row>

          <div className="d-flex gap-2 mt-4">
            <Button onClick={saveSettings} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" className="me-2" /> : <FaSave className="me-2" />}
              Save Settings
            </Button>
            <Button variant="outline-primary" onClick={testSettings} disabled={testing || !settings.has_api_key}>
              {testing ? <Spinner animation="border" size="sm" className="me-2" /> : <FaVial className="me-2" />}
              Test Connection
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

export default AISettings;

